import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { EAllowStatus, ICountryGroup } from '~/src/dash/api/console-api';
import {
  commonRegionGroupsIDs,
  generateCountryGroupOptionsList,
  getCountryGroupsByIDs,
} from '~/src/dash/api/country-group';
import { ISelectOption, ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { CreateRegionModal } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor/CreateRegionModal';
import { EditRegionModal } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor/EditRegionModal';
import { Col, Cols, Row, Rows, spacing } from '~/src/dash/publicaUI';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const generateSelectPresets = (groups: ICountryGroup[]): ISelectPreset[] => {
  const presets: ISelectPreset[] = [];
  groups.forEach((group) => {
    presets.push({
      label: group.Name,
      subset: new Set(group.CountryIsos.split(',').map((g) => g.toLowerCase())),
    });
  });
  return presets;
};

export const RegionsEditor = () => {
  const { session } = useBaseStore();
  const { object, defaultObject, lists } = useFormBuilderStore();

  const [reactCounter, setReactCounter] = useState(1);
  const [regionOptions, setRegionOptions] = useState<ISelectOption[]>(lists.CountryGroups);
  const [selectedIDs, setSelectedIDs] = useState<Set<number>>(
    new Set(object.TargetingCountryGroupIDs),
  );
  const [status, setStatus] = useState<EAllowStatus>(object.TargetingCountryGroupIDsIsAllowed);
  const [commonRegionGroups, setCommonRegionGroups] = useState<ISelectPreset[]>([]);

  useEffect(() => {
    loadCommonRegions();
    return () => {
      object.TargetingCountryGroupIDs = defaultObject.TargetingCountryGroupIDs;
      object.TargetingCountryGroupIDsIsAllowed = defaultObject.TargetingCountryGroupIDsIsAllowed;
    };
  }, []);

  const loadCommonRegions = () => {
    getCountryGroupsByIDs(commonRegionGroupsIDs, session.publisher.ID)
      .then((result) => {
        if (!isEmpty(result) && !isEmpty(result[0])) {
          setCommonRegionGroups(generateSelectPresets(result[0] || []));
        }
      })
      .catch((err) => {
        console.error(err);
        setCommonRegionGroups([]);
      });
  };

  const handleApplySelect = (selectedIDs: Set<number>) => {
    object.TargetingCountryGroupIDs = Array.from(selectedIDs);
    setSelectedIDs(cloneDeep(selectedIDs));
  };

  const handleToggleStatus = () => {
    const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
    object.TargetingCountryGroupIDsIsAllowed = newStatus;
    setStatus(newStatus);
  };

  const handleCreationSuccess = (newCountryGroupID: number) => {
    generateCountryGroupOptionsList(session)
      .then((result) => {
        lists.CountryGroups = result;
        const clonedSelected = cloneDeep(selectedIDs);
        clonedSelected.add(newCountryGroupID);
        unstable_batchedUpdates(() => {
          setRegionOptions(result);
          setSelectedIDs(clonedSelected);
          setReactCounter(reactCounter + 1);
        });
      })
      .catch((err) => {
        console.error(err);
        setRegionOptions([]);
        setSelectedIDs(new Set([]));
        setReactCounter(reactCounter + 1);
      });
  };

  const handleDeleteSuccess = (deletedRegion: ICountryGroup) => {
    const clonedSelectedIDs = cloneDeep(selectedIDs);
    generateCountryGroupOptionsList(session)
      .then((result) => {
        lists.CountryGroups = result;
        unstable_batchedUpdates(() => {
          setRegionOptions(result);
          setReactCounter(reactCounter + 1);
          if (clonedSelectedIDs.has(deletedRegion.ID)) {
            clonedSelectedIDs.delete(deletedRegion.ID);
            setSelectedIDs(clonedSelectedIDs);
          }
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setRegionOptions([]);
          setSelectedIDs(new Set([]));
          setReactCounter(reactCounter + 1);
        });
      });
  };

  const handleUpdateSuccess = () => {
    generateCountryGroupOptionsList(session)
      .then((result) => {
        lists.CountryGroups = result;
        unstable_batchedUpdates(() => {
          setRegionOptions(result);
          setReactCounter(reactCounter + 1);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setRegionOptions([]);
          setSelectedIDs(new Set([]));
          setReactCounter(reactCounter + 1);
        });
      });
  };

  const handleUpdateCancel = () => {
    setReactCounter(reactCounter + 1);
  };

  return (
    <Rows noPadding>
      <Row noPadding height={40}>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col flex style={{ maxWidth: '480px' }}>
            <IndependentSelect
              enableLabelTooltip
              handleApplySelect={handleApplySelect}
              listLabel={'Regions'}
              options={regionOptions}
              placeholder='Select A Region'
              selectedOptionIDs={selectedIDs}
              singularLabel='Region'
              key={reactCounter}
              maxWidth={480}
            />
          </Col>
          <Col>
            <IncludeExcludeToggle
              status={status}
              handleToggleStatus={handleToggleStatus}
              key='TargetingCountryGroupIDsIsAllowed'
            />
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
          <Col width={96}>
            <CreateRegionModal
              handleSuccess={handleCreationSuccess}
              isDisabled={isEmpty(commonRegionGroups)}
              commonRegionGroups={commonRegionGroups}
              key={reactCounter}
            />
          </Col>
          <Col width={96}>
            <EditRegionModal
              handleSuccess={handleUpdateSuccess}
              handleCancel={handleUpdateCancel}
              handleDelete={handleDeleteSuccess}
              key={reactCounter}
              commonRegionGroups={commonRegionGroups}
              isDisabled={isEmpty(regionOptions) || isEmpty(commonRegionGroups)}
            />
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
